import { Card, theme } from 'antd';
import type { ReactNode } from 'react';

export type StepState = 'active' | 'completed' | 'inactive';

interface Props {
  index: number;
  title: string;
  state: StepState;
  children: ReactNode;
  /** 右上角：清除全部选项等 */
  extra?: ReactNode;
  /** inactive 时显示在内容区上方的占位提示（米思米的"请先选择参数"） */
  placeholder?: string;
}

/**
 * 步进式步骤卡片（参考米思米选型工具的纵向步进布局）：所有步骤同时展示，
 * 未到的步骤左侧圆圈灰空、内容区淡化禁用；当前步骤蓝实心；已完成蓝勾。
 */
export function StepCard({ index, title, state, children, extra, placeholder }: Props) {
  const { token } = theme.useToken();
  const isInactive = state === 'inactive';
  const isCompleted = state === 'completed';

  const circleBase = {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
  } as const;

  const circle =
    state === 'active' || isCompleted ? (
      <span
        style={{
          ...circleBase,
          background: token.colorPrimary,
          color: '#fff',
        }}
      >
        {isCompleted ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M2.5 7.2 L5.6 10.3 L11.5 4"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        ) : (
          index
        )}
      </span>
    ) : (
      <span
        style={{
          ...circleBase,
          background: 'transparent',
          color: token.colorTextDisabled,
          border: `1px solid ${token.colorBorder}`,
        }}
      >
        {index}
      </span>
    );

  // 全步骤统一灰系（参考米思米：active/inactive 仅色阶微差，避免白/灰跳跃）
  const cardBg = isInactive ? token.colorFillSecondary : token.colorFillQuaternary;

  return (
    <Card
      size="small"
      style={{
        marginBottom: 12,
        background: cardBg,
        borderColor: isInactive ? token.colorBorderSecondary : token.colorBorder,
      }}
      styles={{ body: { padding: 16 } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        {circle}
        <span
          style={{
            marginLeft: 8,
            fontWeight: 600,
            color: isInactive ? token.colorTextDisabled : token.colorText,
            fontSize: 15,
          }}
        >
          {title}
        </span>
        {extra && (
          <div style={{ marginLeft: 'auto', opacity: isInactive ? 0.5 : 1 }}>{extra}</div>
        )}
      </div>
      {isInactive && placeholder ? (
        <div
          style={{
            color: token.colorTextDisabled,
            background: token.colorBgContainer,
            border: `1px dashed ${token.colorBorderSecondary}`,
            padding: 16,
            borderRadius: 4,
            fontSize: 13,
          }}
        >
          {placeholder}
        </div>
      ) : (
        <div
          style={{
            opacity: isInactive ? 0.55 : 1,
            pointerEvents: isInactive ? 'none' : 'auto',
            userSelect: isInactive ? 'none' : 'auto',
          }}
        >
          {children}
        </div>
      )}
    </Card>
  );
}
